import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
  Chip,
  Avatar,
  IconButton,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Alert,
  TextField,
  FormControlLabel,
  Checkbox,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  SelectAll as SelectAllIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import LostItemCard from './LostItemCard';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { 
  useLostItems, 
  useDeleteLostItem, 
  useBulkDeleteLostItems, 
  useBulkUpdateLostItems,
  useItemStatistics,
  type LostItemsQueryParams 
} from '../hooks/useApi';
import { useNotification } from '../contexts/NotificationContext';

const ITEMS_PER_PAGE = 12;
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const LostItemListNew: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const { showError } = useNotification();

  // State
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'name' | 'found_at' | 'registered_at' | 'status'>('registered_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<'delete' | 'update' | null>(null);

  // Query params
  const queryParams: LostItemsQueryParams = useMemo(() => ({
    limit: ITEMS_PER_PAGE,
    offset: (currentPage - 1) * ITEMS_PER_PAGE,
    ...(searchTerm && { search: searchTerm }),
    ...(startDate && { found_at_start: startDate }),
    ...(endDate && { found_at_end: endDate }),
    ...(selectedStatuses.length > 0 && { status: selectedStatuses.join(',') }),
    sort_by: sortBy,
    sort_order: sortOrder,
  }), [currentPage, searchTerm, startDate, endDate, selectedStatuses, sortBy, sortOrder]);

  const { data, isLoading, error } = useLostItems(queryParams);
  const { data: statistics } = useItemStatistics();
  const deleteItemMutation = useDeleteLostItem();
  const bulkDeleteMutation = useBulkDeleteLostItems();
  const bulkUpdateMutation = useBulkUpdateLostItems();

  const items = useMemo(() => data?.items || [], [data]);
  const totalItems = data?.total_items || 0;

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handleStatusChange = (status: string, checked: boolean) => {
    if (checked) {
      setSelectedStatuses(prev => [...prev, status]);
    } else {
      setSelectedStatuses(prev => prev.filter(s => s !== status));
    }
  };

  const handleDeleteClick = (itemId: string) => {
    setItemToDelete(itemId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (itemToDelete) {
      try {
        await deleteItemMutation.mutateAsync(itemToDelete);
        setDeleteDialogOpen(false);
        setItemToDelete(null);
      } catch (error) {
        console.error('Delete error:', error);
      }
    }
  };

  const handleItemSelect = (itemId: string, checked: boolean) => {
    if (checked) {
      setSelectedItems(prev => [...prev, itemId]);
    } else {
      setSelectedItems(prev => prev.filter(id => id !== itemId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItems(items.map(item => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleBulkAction = (action: 'delete' | 'update') => {
    if (selectedItems.length === 0) {
      showError('アイテムを選択してください');
      return;
    }
    setBulkAction(action);
    setBulkActionDialogOpen(true);
  };

  const handleBulkDelete = async () => {
    try {
      await bulkDeleteMutation.mutateAsync({ item_ids: selectedItems });
      setSelectedItems([]);
      setBulkActionDialogOpen(false);
    } catch (error) {
      console.error('Bulk delete error:', error);
    }
  };

  const handleBulkUpdate = async (updateData: any) => {
    try {
      await bulkUpdateMutation.mutateAsync({ 
        item_ids: selectedItems, 
        update_data: updateData 
      });
      setSelectedItems([]);
      setBulkActionDialogOpen(false);
    } catch (error) {
      console.error('Bulk update error:', error);
    }
  };

  const handleSortChange = (newSortBy: typeof sortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const canEdit = useMemo(() => userProfile && (userProfile.roles.includes('staff') || userProfile.roles.includes('admin')), [userProfile]);
  const canDelete = useMemo(() => userProfile && userProfile.roles.includes('admin'), [userProfile]);

  // Statistics - use API statistics if available, otherwise calculate from current items
  const statusData = useMemo(() => statistics?.status_distribution?.map(item => ({
    name: item.status,
    value: item.count
  })) || Object.entries(items.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>)).map(([name, value]) => ({ name, value })), [statistics, items]);

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        {!currentUser && (
          <Alert severity="info" sx={{ mb: 3 }}>
            ログインすると、アイテムの登録・編集・削除ができます。
          </Alert>
        )}
        {/* Header */}
                {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            落とし物管理
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Sort Button */}
            <Button
              variant="outlined"
              startIcon={<SortIcon />}
              onClick={() => {
                const options = [
                  { label: '登録日時', value: 'registered_at' },
                  { label: '発見日時', value: 'found_at' },
                  { label: '名前', value: 'name' },
                  { label: 'ステータス', value: 'status' },
                ];
                // Create a simple sort menu (you could implement a proper dropdown here)
                const nextIndex = options.findIndex(opt => opt.value === sortBy) + 1;
                const nextSort = options[nextIndex % options.length];
                handleSortChange(nextSort.value as typeof sortBy);
              }}
            >
              {sortBy === 'registered_at' ? '登録日時' : 
               sortBy === 'found_at' ? '発見日時' :
               sortBy === 'name' ? '名前' : 'ステータス'} 
              ({sortOrder === 'asc' ? '昇順' : '降順'})
            </Button>

            {/* Bulk Actions */}
            {selectedItems.length > 0 && canDelete && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleBulkAction('delete')}
              >
                選択削除 ({selectedItems.length})
              </Button>
            )}
            
            {selectedItems.length > 0 && canEdit && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => handleBulkAction('update')}
              >
                一括更新 ({selectedItems.length})
              </Button>
            )}

            {/* Select All/Clear */}
            {items.length > 0 && (canEdit || canDelete) && (
              <Button
                variant="outlined"
                startIcon={selectedItems.length === items.length ? <ClearIcon /> : <SelectAllIcon />}
                onClick={() => handleSelectAll(selectedItems.length !== items.length)}
              >
                {selectedItems.length === items.length ? '選択解除' : '全選択'}
              </Button>
            )}

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="card">
                <ViewModuleIcon />
              </ToggleButton>
              <ToggleButton value="list">
                <ViewListIcon />
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setFilterDialogOpen(true)}
            >
              フィルター
            </Button>
          </Box>
        </Box>

        {/* Search Bar */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder="名前や説明で検索..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              sx={{ minWidth: 'auto', px: 3 }}
            >
              検索
            </Button>
          </Box>
        </Paper>

        {/* Statistics */}
        {statusData.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                統計情報
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value, percent }) => `${name}: ${value} (${percent ? (percent * 100).toFixed(0) : 0}%)`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {isLoading ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
            {Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
              <Card key={index} sx={{ height: 300 }}>
                <Skeleton variant="rectangular" height={200} />
                <CardContent>
                  <Skeleton variant="text" height={32} width="80%" />
                  <Skeleton variant="text" height={24} width="60%" />
                  <Skeleton variant="text" height={20} width="40%" />
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            エラーが発生しました: {error.message}
          </Alert>
        ) : items.length === 0 ? (
          <Alert severity="info">
            落とし物が見つかりませんでした。
          </Alert>
        ) : viewMode === 'card' ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
            {items.map((item) => (
              <LostItemCard
                key={item.id}
                id={item.id}
                name={item.name}
                status={item.status}
                found_location={item.found_location}
                found_at={item.found_at}
                store_name={item.store?.name}
                media_urls={item.media_urls || []}
                onView={() => navigate(`/items/${item.id}`)}
                onEdit={canEdit ? () => navigate(`/items/${item.id}/edit`) : undefined}
                onDelete={canDelete ? () => handleDeleteClick(item.id) : undefined}
              />
            ))}
          </Box>
        ) : (
          <Paper>
            {items.map((item, index) => (
              <Box
                key={item.id}
                sx={{
                  p: 2,
                  borderBottom: index < items.length - 1 ? 1 : 0,
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {(canEdit || canDelete) && (
                  <Checkbox
                    checked={selectedItems.includes(item.id)}
                    onChange={(e) => handleItemSelect(item.id, e.target.checked)}
                  />
                )}
                {item.media_urls && item.media_urls[0] && (
                  <Avatar
                    src={item.media_urls[0]}
                    alt={item.name}
                    sx={{ width: 60, height: 60 }}
                    variant="rounded"
                  />
                )}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="div">
                    {item.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.description || 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.found_location} • {new Date(item.found_at).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })}
                  </Typography>
                </Box>
                <Chip
                  label={item.status}
                  size="small"
                  color={item.status === 'Found' ? 'success' : item.status === 'Returned' ? 'primary' : 'default'}
                />
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/items/${item.id}`)}
                    sx={{ mr: 1 }}
                  >
                    <ViewIcon fontSize="small" />
                  </IconButton>
                  {canEdit && (
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/items/${item.id}/edit`)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  {canDelete && (
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(item.id)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}
          </Paper>
        )}

        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
            size="large"
          />
        </Box>
      </Box>

      {/* Add Button */}
      {canEdit && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
          }}
          onClick={() => navigate('/register')}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>フィルター</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="発見日（開始）"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="発見日（終了）"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                ステータス
              </Typography>
              {['Found', 'Claimed', 'Returned', 'Disposed'].map((status) => (
                <FormControlLabel
                  key={status}
                  control={
                    <Checkbox
                      checked={selectedStatuses.includes(status)}
                      onChange={(e) => handleStatusChange(status, e.target.checked)}
                    />
                  }
                  label={status}
                />
              ))}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialogOpen(false)}>
            キャンセル
          </Button>
          <Button
            onClick={() => {
              setFilterDialogOpen(false);
              handleSearch();
            }}
            variant="contained"
          >
            適用
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>削除確認</DialogTitle>
        <DialogContent>
          <Typography>
            本当にこの落とし物を削除しますか？この操作は取り消せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            キャンセル
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteItemMutation.isPending}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog
        open={bulkActionDialogOpen}
        onClose={() => setBulkActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {bulkAction === 'delete' ? '一括削除確認' : '一括更新'}
        </DialogTitle>
        <DialogContent>
          {bulkAction === 'delete' ? (
            <Typography>
              選択された{selectedItems.length}件のアイテムを削除しますか？
              この操作は取り消せません。
            </Typography>
          ) : (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Typography>
                選択された{selectedItems.length}件のアイテムを一括更新します。
              </Typography>
              <TextField
                fullWidth
                label="ステータス"
                select
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) {
                    handleBulkUpdate({ status: e.target.value });
                  }
                }}
              >
                <option value="">変更しない</option>
                <option value="Found">Found</option>
                <option value="Claimed">Claimed</option>
                <option value="Returned">Returned</option>
                <option value="Disposed">Disposed</option>
              </TextField>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActionDialogOpen(false)}>
            キャンセル
          </Button>
          {bulkAction === 'delete' && (
            <Button
              onClick={handleBulkDelete}
              color="error"
              variant="contained"
              disabled={bulkDeleteMutation.isPending}
            >
              削除
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LostItemListNew;
